import LogCannon from '@ace-clublabs/log-cannon';
import { ErrorCode, IConfigEntry, IProcessPaymentWithManifestRequest, PaymentType } from '@ace-clublabs/payment-types';
import {
  Alert,
  AlertTypography,
  AppHeaderBackButton,
  Box,
  Checkbox,
  CheckboxGroup,
  CheckboxTypography,
  Form,
  Icon,
  Link,
  Modal,
  ModalActionButton,
  ModalActions,
  ModalCloseIconButton,
  ModalContent,
  ModalTitle,
  StickyButton,
  TextField,
  Typography,
  useTextValidator,
} from '@ace-clublabs/shared-component-library';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import echeckImage from '../assets/echeck-find.png';
import { PaymentReview } from '../containers/PaymentReview';
import { Pages } from '../shared/pages';
import { navigatePaymentFormBack } from '../utils/paymentNavigationHelper';
import { MANIFEST_TTL_MS } from '../constants';
import { useGlobalState } from '../shared/state';
import { ErrorSeverity, ErrorType } from '../shared/types';
import { getTermsUrl } from '../utils/methods';
import { getMyAccountURL } from '@ace-clublabs/payments-helper';
import { ProcessPaymentHelper } from '../utils/processPaymentHelper';
import UrlHelper from '../utils/urlHelper';
import Header from './Header';
import PaymentHeader from './PaymentHeader';
import { PaymentMethodsAuthorizationModal } from './PaymentMethodsAuthorizationModal';

export interface ECheckFormProps {
  manifestId: string;
  amount: number;
  returnUrl: string;
  isScheduled: boolean | undefined;
  autoPay?: boolean;
  appId?: string;
  scheduledDate?: string;
  paymentFormSubtitle?: string;
  setParentProcessing?: any;
  softErrorCode?: ErrorCode;
}

const ECheckForm: React.FC<ECheckFormProps> = (props) => {
  const navigate = useNavigate();
  const {
    manifestId,
    amount,
    returnUrl,
    autoPay,
    appId,
    scheduledDate,
    isScheduled,
    paymentFormSubtitle,
    setParentProcessing,
    softErrorCode,
  } = props;
  const [manifest] = useGlobalState('manifest');
  const [redirectMap, setRedirected] = useGlobalState('redirectMap');
  const [, setPaymentProcessing] = useGlobalState('paymentProcessing');
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [paymentMethodsSkipped, setpaymentMethodsSkipped] = useGlobalState('paymentMethodsSkipped');
  const [, setError] = useGlobalState('error') || [];
  const [manifestTimestamp] = useGlobalState('manifestTimestamp') || [];
  const [isProcessing, setProcessing] = useState(true);
  const [isPaymentReview, setPaymentReview] = useState(false);
  const [last4, setLast4] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEcheckModal, setShowEcheckModal] = useState(false);
  const config = manifest?.clientConfig || ({} as IConfigEntry);
  const [storePaymentChecked, setStorePaymentChecked] = useState(false);
  const [data, setData] = useState(undefined);
  const [showPaymentAuthorization, setShowPaymentAuthorization] = useState(false);
  const [paymentStatusUnknown, setPaymentStatusUnknown] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [routingNumberValue, setRoutingNumberValue] = useState('');
  const [accountNumberValue, setAccountNumberValue] = useState('');
  const [confirmAccountNumberValue, setConfirmAccountNumberValue] = useState('');
  const allowStoredPayment = config?.allowStoredPayment;

  const [routingNumberApiError, setRoutingNumberApiError] = useState('');
  const [accountNumberApiError, setAccountNumberApiError] = useState('');
  const [softErrorMessage, setSoftErrorMessage] = useState('');

  const nameValidator = useTextValidator({
    required: false,
    maxLength: 25,
    capitalize: false,
    trim: false,
    validations: [
      ({ value }) => ({
        value,
        isValid: value.length > 0,
        message: 'Name on the account is required.',
      }),
      ({ value }) => ({
        value,
        isValid: !/\d{5,}/.test(value),
        message: 'Invalid name on the account.',
      }),
    ],
  });

  const routingNumberValidator = useTextValidator({
    required: false,
    minLength: 9,
    maxLength: 9,
    filterCharacterPattern: '[^0-9]',
    capitalize: false,
    trim: false,
    allowSpaces: false,
    validations: [
      ({ value }) => ({
        value,
        isValid: value.length > 0,
        message: 'Routing number is required.',
      }),
    ],
    fieldMustBeExactLengthValidationMessage: 'Routing number should be 9 digits.',
  });

  const accountNumberValidator = useTextValidator({
    required: false,
    maxLength: 17,
    filterCharacterPattern: '[^0-9]',
    capitalize: false,
    trim: false,
    allowSpaces: false,
    validations: [
      ({ value }) => ({
        value,
        isValid: value.length > 0,
        message: 'Account number is required.',
      }),
      ({ value }) => ({
        value,
        isValid: value.length === 0 || value.length >= 5,
        message: 'Account number should be at least 5 digits.',
      }),
    ],
  });

  const confirmAccountNumberValidator = useTextValidator({
    required: false,
    maxLength: 17,
    filterCharacterPattern: '[^0-9]',
    capitalize: false,
    trim: false,
    allowSpaces: false,
    validations: [
      ({ value }) => ({
        value,
        isValid: value.length > 0,
        message: 'Re-type account number is required.',
      }),
      ({ value }) => ({
        value,
        isValid: value.length === 0 || value.length >= 5,
        message: 'Re-type account number should be at least 5 digits.',
      }),
      ({ value }) => ({
        value,
        isValid: value.length === 0 || value === accountNumberValue,
        message: 'Account number does not match.',
      }),
    ],
  });

  const confirmMismatch = confirmAccountNumberValue.length >= 5 && confirmAccountNumberValue !== accountNumberValue;

  useEffect(() => {
    if (!softErrorCode) {
      setSoftErrorMessage('');
      return;
    }
    switch (softErrorCode) {
      case ErrorCode.ChargeDeclined:
        setSoftErrorMessage('Charge declined, please use a different payment method.');
        break;
      case ErrorCode.InsufficientFunds:
        setSoftErrorMessage('Insufficient funds, please try again.');
        break;
      case ErrorCode.InvalidRoutingNumber:
        setSoftErrorMessage('Invalid routing number.');
        break;
      case ErrorCode.InvalidAccountNumber:
        setSoftErrorMessage('Invalid account number.');
        break;
      case ErrorCode.NetworkTimeout:
        setSoftErrorMessage('A network error occurred. Please try again.');
        break;
      default:
        setSoftErrorMessage('There was an issue with your payment. Please try again.');
    }
  }, [softErrorCode]);

  useEffect(() => {
    setTimeout(() => {
      setProcessing(true);
      setParentProcessing({
        status: true,
        text: 'Your checkout is loading',
        cindy: true,
        displayCheckmark: true,
        callback: () => {
          setProcessing(false);
        },
      });
    }, 1);
  }, []);

  const redirect = (url: string) => {
    setRedirected({ ...redirectMap, [manifestId]: url });
    navigate(`/redirect/${manifestId}`);
  };

  const handleSoftError = (error: { code: ErrorCode }) => {
    switch (error.code) {
      case ErrorCode.InvalidRoutingNumber:
        setRoutingNumberApiError('Invalid routing number.');
        break;
      case ErrorCode.InvalidAccountNumber:
        setAccountNumberApiError('Invalid account number.');
        break;
      case ErrorCode.StorePaymentError:
        setAccountNumberApiError('This account number is invalid. Please try another payment method.');
        break;
      case ErrorCode.NetworkTimeout:
        if (appId === 'manage-saved-payments') {
          setAccountNumberApiError('Your payment method could not be saved. Please try again.');
        }
        break;
      default:
        setShowModal(true);
        break;
    }
  };

  const submitPayment = async (data: any) => {
    setPaymentStatusUnknown(false);
    try {
      const isManifestStale = !!manifestTimestamp && Date.now() - manifestTimestamp > MANIFEST_TTL_MS;
      if (isManifestStale) {
        throw { type: ErrorType.SESSION_TIMEOUT, message: 'Manifest is stale' };
      }
      setParentProcessing({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: false,
      });
      setProcessing(true);
      const { name, accountNumber, routingNumber } = data;
      const processPaymentBody = {
        manifestId: manifestId,
        echeck: {
          accountHolderName: name,
          accountNumber,
          routingNumber,
        },
        paymentType: PaymentType.Echeck,
        scheduledDate: scheduledDate,
      } as IProcessPaymentWithManifestRequest;

      if ((allowStoredPayment && storePaymentChecked) || appId === 'manage-saved-payments')
        processPaymentBody.storePayment = {
          enable: true,
        };

      setPaymentProcessing(true);
      const { transactionId } = await ProcessPaymentHelper.processPayment(processPaymentBody);
      setParentProcessing({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: true,
        callback: () => {
          const returnUrlWithTransactionId = UrlHelper.addQueryParam(returnUrl, {
            transactionId,
          });
          redirect(returnUrlWithTransactionId);
        },
      });
    } catch (error: any) {
      setParentProcessing({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: true,
        callback: () => {
          setProcessing(false);
        },
      });
      if (error?.type === ErrorSeverity.SOFT && error?.message === 'payment_status_unknown') {
        setProcessing(false);
        setPaymentReview(false);
        LogCannon.blast('Payment status unknown after echeck payment — showing inline message', {
          appId,
          component: 'ECheckForm',
          manifestId,
        });
        setPaymentStatusUnknown(true);
      }
      else if (error.type === ErrorSeverity.SOFT) {
        setProcessing(false);
        setPaymentReview(false);
        handleSoftError(error);
      }
      else {
        const isExpired = error?.type === ErrorType.SESSION_TIMEOUT;
        let errorMessage = 'ERROR: Submit ECheck Payment Exception';

        if (error.code === ErrorCode.TooManyRequests) {
          errorMessage = 'ERROR: Submit ECheck Payment Rate Limited Exception';
          setError({ errorType: ErrorType.RATE_LIMITED, errorMessage: '' });
        } else if (isExpired) {
          setError({ errorType: ErrorType.SESSION_TIMEOUT, errorMessage: error?.message ?? '' });
        }

        LogCannon.blast(isExpired ? 'Navigating to Session Error' : 'Navigating to Error', {
          appId,
          component: 'ECheckForm',
          errorMessage,
          error: typeof error === 'string' ? `${error}` : error,
          manifestId,
          ...(isExpired && { manifestExpiredAfterMs: Date.now() - manifestTimestamp!, expiredManifestId: manifestId }),
        });
        navigate(Pages.Error);
      }
    }
  };

  const renderPaymentReview = manifest?.clientConfig?.renderPaymentReview;

  const handleSubmit = async (data: any) => {
    setData(data);
    if (renderPaymentReview && amount > 0) {
      setPaymentReview(true);
    } else {
      submitPayment(data);
    }
  };

  const modalDialog = (
    <Modal
      open={showModal && !isProcessing}
      onClose={() => setShowModal(false)}
      variants={{
        xs: 'bottom-sheet',
        sm: 'regular',
      }}
    >
      <ModalTitle id={`error-modal-title`} sx={{ marginRight: '2rem' }}>
        Please try again.
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowModal(false)} />
      </ModalTitle>

      <ModalContent id="error-modal-content">
        <Typography variant="b1Regular">There was an unexpected error.</Typography>
      </ModalContent>
      <ModalActions
        actionLayout={{
          sm: 'fullWidth',
          md: 'fullWidth',
          lg: 'rightAlign',
          xl: 'rightAlign',
        }}
      >
        <ModalActionButton label="OK" onClick={() => setShowModal(false)} />
      </ModalActions>
    </Modal>
  );

  const echeckModal = (
    <Modal
      open={showEcheckModal}
      onClose={() => setShowEcheckModal(false)}
      variants={{
        xs: 'bottom-sheet',
        sm: 'regular',
      }}
    >
      <ModalTitle id={`modal-title-echeck`} sx={{ marginRight: '2rem' }}>
        Where do I find this?
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowEcheckModal(false)} />
      </ModalTitle>

      <ModalContent id="modal-content-echeck">
        <Typography variant="b1Regular">
          You can find your routing and account number at the bottom of your checks. The first set of numbers are the
          routing number, and the second set is your account number.
        </Typography>
        <div className="echeck-img-div">
          <img src={echeckImage} className="echeck-img" alt="echeck" />
        </div>
      </ModalContent>
      <ModalActions
        actionLayout={{
          sm: 'fullWidth',
          md: 'fullWidth',
          lg: 'rightAlign',
          xl: 'rightAlign',
        }}
      >
        <ModalActionButton label="Got it" onClick={() => setShowEcheckModal(false)} />
      </ModalActions>
    </Modal>
  );

  const handleStorePaymentCheck = () => setStorePaymentChecked((sp) => !sp);

  const echeckModalHandler = (e: any) => {
    e.preventDefault();
    setShowEcheckModal(true);
  };

  const isScheduledPayment: boolean = !!isScheduled || !!manifest?.isScheduled;
  const authText = isScheduledPayment || !(amount > 0) || renderPaymentReview ? 'Continue' : 'Pay now';
  const isMap = appId === 'manage-autopay';
  const isStorePaymentFlow = amount === 0 && !autoPay;
  const mapChangeFlow = isMap && manifest?.renderTsAndCs?.render;
  const mapNewEnrollFlow = isMap && !manifest?.renderTsAndCs?.render;
  const renderTermsAndPaymentAuth = mapChangeFlow && paymentMethodsSkipped;
  const willVisitPaymentReview = renderPaymentReview && amount > 0;
  const renderPaymentAuth =
    !renderTermsAndPaymentAuth &&
    ((mapNewEnrollFlow && amount === 0) || mapChangeFlow || !(willVisitPaymentReview || isStorePaymentFlow));

  const handleBack = () => {
    navigatePaymentFormBack(navigate, manifest, {
      softErrorCode,
      paymentMethodsSkipped,
      onClearPaymentMethodsSkipped: () => setpaymentMethodsSkipped(false),
    });
  };

  const amountDue = amount > 0;
  const buttonText = !isScheduled && amountDue && !renderPaymentReview ? 'Pay now' : 'Continue';

  const allowSubmitButton =
    nameValue.length > 0 &&
    !/\d{5,}/.test(nameValue) &&
    routingNumberValue.length === 9 &&
    accountNumberValue.length >= 5 &&
    confirmAccountNumberValue === accountNumberValue &&
    !routingNumberApiError &&
    !accountNumberApiError;

  return (
    <>
      {isPaymentReview && !isProcessing && (
        <PaymentReview
          amount={manifest?.amount!}
          paymentDetails={{
            paymentType: PaymentType.Echeck,
            echeck: {
              last4,
              routingNumber,
            },
          }}
          scheduledDate={scheduledDate}
          pit={manifest?.pit ? { ...manifest.pit } : undefined}
          onSubmit={(e) => {
            submitPayment(data);
          }}
          onBack={() => setPaymentReview(false)}
        />
      )}

      <div className="payment-form" style={isProcessing || isPaymentReview ? { display: 'none' } : {}}>
        <div className="payment-form-cream-section">
          <div className="payment-form-body">
            <Header />
            {!isMobileApp && <AppHeaderBackButton id="back-button" onClick={handleBack} />}
            <Box className="secure-checkout">
              <Icon iconName="lock-keyhole-solid" className="lock-icon" />
              <span>Secure checkout</span>
            </Box>
            <PaymentHeader paymentType={PaymentType.Echeck} amount={amount} autoPay={autoPay} />
            <Typography variant="b1Regular" data-quid="echeck-form-subtitle">
              {paymentFormSubtitle}
            </Typography>
          </div>
        </div>
        <div className="payment-form-body echeck-form">
          {softErrorMessage && (
            <Alert severity="error" id="echeck-soft-error-alert" displayCloseButton={false}>
              <AlertTypography>{softErrorMessage}</AlertTypography>
            </Alert>
          )}
          {paymentStatusUnknown && (
            <Alert severity="warning" id="payment-status-unknown-alert" displayCloseButton={false}>
              <AlertTypography >
                Payment submitted but there was an issue confirming it — please check{' '}
                <Link href={getMyAccountURL()} target="_blank" rel="noopener noreferrer" >
                  My Account
                </Link>{' '}
                before retrying to avoid a duplicate charge.
              </AlertTypography>
            </Alert>
          )}
          <Form
            component="form"
            onSubmit={() => {
              if (!allowSubmitButton) return;
              setLast4(accountNumberValue.slice(-4));
              setRoutingNumber(routingNumberValue);
              handleSubmit({
                name: nameValue,
                routingNumber: routingNumberValue,
                accountNumber: accountNumberValue,
              });
            }}
          >
            <Box id="echeck-form-inner-container">
              <div className="name-on-account-field">
                <TextField
                  id="name"
                  name="name"
                  label="Name on the account"
                  fullWidth={true}
                  type="text"
                  showClearButton={true}
                  value={nameValue}
                  validator={nameValidator}
                  onChange={(e) => setNameValue(e.target.value)}
                  onClearButtonClick={() => setNameValue('')}
                />
              </div>
              <div className="routing-number-field">
                <TextField
                  id="routingNumber"
                  name="routingNumber"
                  label="Routing number"
                  fullWidth={true}
                  value={routingNumberValue}
                  validator={routingNumberValidator}
                  error={routingNumberApiError ? true : undefined}
                  validationErrorMessage={routingNumberApiError || undefined}
                  inputMode="numeric"
                  type="text"
                  showClearButton={true}
                  onChange={(e) => {
                    setRoutingNumberValue(e.target.value);
                    if (routingNumberApiError) setRoutingNumberApiError('');
                  }}
                  onClearButtonClick={() => {
                    setRoutingNumberValue('');
                    if (routingNumberApiError) setRoutingNumberApiError('');
                  }}
                />
                <Link id="routing-number-desc-link" component="button" onClick={echeckModalHandler}>
                  Where do I find this?
                </Link>
              </div>
              <div className="account-number-field">
                <TextField
                  id="accountNumber"
                  name="accountNumber"
                  label="Account number"
                  fullWidth={true}
                  value={accountNumberValue}
                  validator={accountNumberValidator}
                  error={accountNumberApiError ? true : undefined}
                  validationErrorMessage={accountNumberApiError || undefined}
                  inputMode="numeric"
                  type="text"
                  showClearButton={true}
                  onChange={(e) => {
                    setAccountNumberValue(e.target.value);
                    if (accountNumberApiError) setAccountNumberApiError('');
                  }}
                  onClearButtonClick={() => {
                    setAccountNumberValue('');
                    if (accountNumberApiError) setAccountNumberApiError('');
                  }}
                />
                <Link id="account-number-desc-link" component="button" onClick={echeckModalHandler}>
                  Where do I find this?
                </Link>
              </div>
              <div className="confirm-account-number-field">
                <TextField
                  id="confirmAccountNumber"
                  name="confirmAccountNumber"
                  label="Re-type account number"
                  fullWidth={true}
                  value={confirmAccountNumberValue}
                  validator={confirmAccountNumberValidator}
                  error={confirmMismatch ? true : undefined}
                  validationErrorMessage={confirmMismatch ? 'Account number does not match.' : undefined}
                  inputMode="numeric"
                  type="text"
                  showClearButton={true}
                  disableFormValue={true}
                  onChange={(e) => setConfirmAccountNumberValue(e.target.value)}
                  onClearButtonClick={() => setConfirmAccountNumberValue('')}
                />
              </div>
              {allowStoredPayment && (
                <CheckboxGroup className="save-this-payment" variant="text">
                  <Checkbox
                    name="store-payment"
                    checked={storePaymentChecked}
                    label={<CheckboxTypography>Save for future payments</CheckboxTypography>}
                    enableForm={false}
                    onClick={() => handleStorePaymentCheck()}
                  />
                </CheckboxGroup>
              )}
              {renderPaymentAuth && (
                <Typography variant="b1Regular" data-quid="payment-authorization-text">
                  By clicking {authText}, I hereby state that I am an authorized user for the above payment method, and
                  have read and agree to the{' '}
                  <Link
                    id="payment-authorization-link"
                    component="button"
                    sx={{ verticalAlign: 'baseline' }}
                    onClick={(e: any) => {
                      setShowPaymentAuthorization(true);
                      e.preventDefault();
                    }}
                    variant="b1Regular"
                  >
                    Payment Authorization
                  </Link>
                  .
                </Typography>
              )}
              {renderTermsAndPaymentAuth && (
                <Typography variant="b2Regular" className="terms-and-conditions">
                  By clicking Continue, I hereby enroll in Auto Pay and that I am an authorized user for the above
                  payment method I have selected, and have read and agree to the{' '}
                  <Link
                    data-id="terms-conditions-link"
                    id="terms-conditions-link"
                    variant="b2Regular"
                    href={getTermsUrl()}
                    target="_blank"
                    sx={{ verticalAlign: 'baseline' }}
                  >
                    Terms & Conditions
                  </Link>{' '}
                  <Typography variant="b2Regular" sx={{ display: 'inline-block' }}>
                    and{' '}
                  </Typography>
                  <Link
                    data-id="terms-conditions-link"
                    variant="b2Regular"
                    component="button"
                    sx={{ verticalAlign: 'baseline' }}
                    onClick={(e: any) => {
                      setShowPaymentAuthorization(true);
                      e.preventDefault();
                    }}
                  >
                    Payment Authorization
                  </Link>
                  .
                </Typography>
              )}

              {showPaymentAuthorization && (
                <PaymentMethodsAuthorizationModal
                  handleClose={() => {
                    setShowPaymentAuthorization(false);
                  }}
                  ctaText={authText}
                ></PaymentMethodsAuthorizationModal>
              )}
            </Box>
            <StickyButton
              id="submit"
              slotProps={{
                primaryButton: {
                  label: buttonText,
                  type: 'submit',
                  disabled: !allowSubmitButton,
                  endIcon: amountDue ? <Icon iconName="lock-keyhole-solid" /> : undefined,
                },
              }}
              renderAmount={amountDue ? amount : undefined}
            />
          </Form>
        </div>
        {modalDialog}
        {echeckModal}
      </div>
    </>
  );
};

export default ECheckForm;
